import React, { Suspense, useCallback, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Redirect, Route, useHistory, useLocation } from "react-router-dom";

import commonClient from "@/apis/commonClient";
import userClient from "@/apis/userClient";
import * as actions from "@/redux/actions";

import $ from "jquery";
import Cookies from "js-cookie";
import authenticationClient from "@/apis/authenticationClient";
import Loading from "@/components/ReactBootstrap/LoaderSpinner/Loading";
import { setLocale } from "@/constants/SensolusMoment";
import { useOrg } from "@/contexts/OrgContext";
import { useCustomBranding } from "@/pages/CustomBranding/useCustomBranding";
import {
  WebSocketProvider,
  useWebSocket,
} from "@/contexts/WebSocketContext";
import { adminRoutes } from "@/containers/App/routes";
import Header from "./Header/Header";
import HelpSidebar from "./HelpSidebar/HelpSidebar";
import Sidebar from "./Sidebar/Sidebar";
import TermOfUse from "./TermOfUse/TermOfUse";
import ImpersonateHeader from "./Header/ImpersonateHeader";

import SensolusWhileLogoIcon from "@images/sensolus/sensolus_logo_white.svg";
import SensolusLogoIcon from "@images/sensolus/sensolus_logo.svg";
import businessIntelligenceClient from "@/apis/businessIntelligenceClient";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import SntDialog from "@/components/SntDialog/SntDialog";
import NotificationSidebar from "@/cleanup/containers/NotificationSidebar/NotificationSidebar";
import AiChatSidebar from "@/cleanup/containers/AiChatSidebar/AiChatSidebar";
import { useAppLanguage } from "@/contexts/AppLanguageContext";
import { RootState, DashboardNotification } from "@/types/RootState";
import type { RouteConfig } from "@/types/RouteConfig";
import { hasPermission } from "@/hooks/usePermissions";
import { UserPermission } from "@/types/generated/UserPermission";

interface AppLayoutState {
  loading: boolean;
  redirect: boolean;
}

interface WebSocketContextValue {
  isConnected: boolean;
  connectionStatus: string;
  lastMessage: unknown;
  sessionId: string | null;
  connect: (jwtToken?: string, csrfToken?: string) => void;
  disconnect: () => void;
  sendMessage: (message: unknown) => void;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
}

const AppLayoutContent: React.FC = () => {
  const [state, setState] = useState<AppLayoutState>({
    loading: true,
    redirect: false,
  });

  const setCookie = useCookies(["_csrf"])[1];
  const sensolusFullScreen = Cookies.get("sensolus-full-screen");
  const dispatch = useDispatch();
  const loginInfo = useSelector((state: RootState) => state.user, shallowEqual);
  const history = useHistory();
  const location = useLocation();
  const orgContext = useOrg();
  const { updateOrg } = (orgContext || {}) as { updateOrg?: (id?: number) => void };
  const [reloadCount, setReloadCount] = useState<number>(0);
  const [isShowTermOfUse, setIsShowTermOfUse] = useState<boolean>(false);
  const [impersonateUser, setImpersonateUser] = useState<Record<string, unknown> | undefined>();
  
  // WebSocket integration
  const { connect, disconnect, isConnected, connectionStatus } = useWebSocket() as WebSocketContextValue;

  const appLanguageContext = useAppLanguage();
  const { appLanguage, setAppLanguage } = (appLanguageContext || {}) as { appLanguage?: string; setAppLanguage?: (lang: string) => void };

  const { menuType, dashboardNotification, preventSidebar } = useSelector(
    (state: RootState) => state.all,
    shallowEqual
  );

  const { getHomeLogoImgSrc, getAdminLogoImgSrc } = useCustomBranding();

  useEffect(() => {
    let src: string | null = null;
    if (loginInfo && loginInfo.custumInfo) {
      if (menuType === "MAIN") {
        src = getHomeLogoImgSrc(loginInfo.custumInfo);
      } else {
        src = getAdminLogoImgSrc(loginInfo.custumInfo);
      }
    }
    if (!src) {
      if (menuType === "MAIN") {
        src = SensolusWhileLogoIcon;
      } else {
        src = SensolusLogoIcon;
      }
    }
    dispatch(actions.setLogoPath(src));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo, menuType]);

  useEffect(() => {
    if (loginInfo) {
      if (loginInfo?.organisation?.organisationType === "NORMAL" && updateOrg) {
        updateOrg(loginInfo.organisation.id);
      }

      setImpersonateUser(loginInfo.impersonateData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo]);

  const reloadPage = useCallback(() => {
    setReloadCount((prevReload) => prevReload + 1);
  }, []);

  useEffect(() => {
    if (appLanguage) {
      document.documentElement.lang = appLanguage === "en-gb" ? "en" : appLanguage;
    }
  }, [appLanguage]);

  useEffect(() => {
    const csrfName = sessionStorage.getItem("csrf_name");
    const csrf = csrfName ? Cookies.get(csrfName) : undefined;

    authenticationClient
      .getUserInfo()
      .then((data) => {
        data.data.csrfToken = csrf;
        const loginData = data.data;

        dispatch(actions.loginSuccess(loginData));
        const languageKey = loginData.language;
        if (setAppLanguage) {
          setAppLanguage(languageKey);
        }
        userClient.getLanguage(languageKey).then((data) => {
          const _lang = data.data;
          dispatch(actions.updateLanguage(_lang));
          setLocale(languageKey, loginData.timezone);
          setState({ loading: false, redirect: false });

          connect(loginData.token, csrf);

          commonClient
            .getMessageOnUserPage()
            .then((data) => {
              dispatch(actions.updateMessage(data.data));
            })
            .catch(() => {});

          if (loginData.termsOfUseInfo && !loginData.termsOfUseInfo.ok) {
            setIsShowTermOfUse(true);
          }
        });
      })
      .catch(() => {
        setState({ loading: false, redirect: true });
      });

    let lastPathName = "";
    const listen = history.listen((location) => {
      const newPath = location.pathname + location.search;
      if (lastPathName !== newPath) {
        lastPathName = newPath;
        dispatch(actions.addHistoryCount());
      }
    });

    return () => {
      listen();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCookie, dispatch, history]);

  useEffect(() => {}, [isConnected, connectionStatus]);

  useEffect(() => {
    return () => {
      console.log("AppLayout unmounting, disconnecting WebSocket...");
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.loading) {
    return null;
  }

  if (state.redirect) {
    if (
      location.pathname &&
      location.pathname.length > 0 &&
      location.pathname !== "/tracked_assets" &&
      location.pathname !== "/"
    ) {
      return (
        <Redirect
          to={
            "/login" +
            (location.search && location.search.length > 0
              ? location.search + "&"
              : "?") +
            "redirect_to=" +
            location.pathname
          }
        />
      );
    }
    return <Redirect to={"/login"} />;
  }

  const getRoute = (obj: RouteConfig, key: number): React.ReactElement => {
    let component = obj;
    if (
      !(
        hasPermission(UserPermission.DEVICE_METADATA_VIEW) ||
        hasPermission(UserPermission.DEVICE_UPDATE)
      ) &&
      obj.path === "/tracked_assets"
    ) {
      component = {
        ...obj,
        component: () => {
          return <></>;
        },
      };
    }

    return (
      <Route
        key={key}
        path={obj.path}
        exact={obj.exact}
        render={() => (
          <Suspense fallback={<Loading />}>
            <div key={reloadCount}>
              {React.cloneElement(component.component() as React.ReactElement<{ reload?: number }>, {
                reload: reloadCount,
              })}
            </div>
          </Suspense>
        )}
      />
    );
  };

  const listMenu = adminRoutes.map((obj, key) => {
    return getRoute(obj, key);
  });

  const helpSideBarCloseEvent = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    $("#help-side-info-panel").hide();
  };

  const saveDashboard = (data: DashboardNotification): void => {
    const descriptor = JSON.stringify({ widgets: data.widgets });
    businessIntelligenceClient
      .update(String(data.id), { descriptor: descriptor, dashboardType: "PLATFORM" })
      .then(() => {
        dispatch(actions.setDashboardNotification(null));
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <div
      className={`wrapper  ${sensolusFullScreen && "full-screen-mode"} ${
        impersonateUser ? "impersonate-mode" : ""
      }`}
    >
      {!preventSidebar && !sensolusFullScreen ? (
        <>
          <ImpersonateHeader
            impersonateUser={impersonateUser}
            onLeaveClicked={() => {
              setImpersonateUser(undefined);
            }}
          />

          <Header reloadPage={reloadPage} />
          <Sidebar />
        </>
      ) : (
        <></>
      )}

      {!preventSidebar ? (
        <div className="main-panel snt-main-panel">
          <div className="content">
            <div className="container-fluid">{listMenu}</div>
          </div>
        </div>
      ) : (
        <></>
      )}

      <div
        id="help-side-info-panel"
        className="right-sidebar sidebar-help side-info-panel slideInRight animated snt-right-sidebar"
        style={{ display: "none" }}
      >
        <HelpSidebar
          orgId={loginInfo.organisation?.id}
          closeEvent={helpSideBarCloseEvent}
        />
      </div>

      <NotificationSidebar />
      <AiChatSidebar />

      <TermOfUse
        isShow={isShowTermOfUse}
        setIsShow={setIsShowTermOfUse}
        loginInfo={loginInfo}
      />

      {dashboardNotification && (
        <SntDialog
          isShow={true}
          onSave={() => saveDashboard(dashboardNotification)}
          onClose={() => {
            dispatch(actions.setDashboardNotification(null));
          }}
          title={"Update the changed dashboard settings"}
          saveTxt={translate(LanguageKey.save_key)}
          closeTxt={translate(LanguageKey.cancel_key)}
          backdrop={"static"}
        >
          {() => {
            return (
              <div>
                Do you want to save the changes made to the settings of{" "}
                <b>{dashboardNotification.title}</b> dashboard?
              </div>
            );
          }}
        </SntDialog>
      )}
    </div>
  );
};

const AppLayout: React.FC = () => {
  return (
    <WebSocketProvider>
      <AppLayoutContent />
    </WebSocketProvider>
  );
};

export default AppLayout;
